import { useEffect, useState } from "react";
import { Avatar, Button, Card, Col, Empty, Row, Spin, Tag, Tooltip } from "antd";
import { CheckSquareOutlined, ClockCircleOutlined, FolderOpenOutlined, ReloadOutlined, TeamOutlined } from "@ant-design/icons";
import Main from "../../components/layouts/Main";
import PageTitle from "../../components/utils/PageTitle";
import { PersonnelService } from "../../services/PersonnelService";
import { catchError } from "../../services/DaoService";
import PersonnelQuickView from "../../components/personnel/PersonnelQuickView";
import { useDetailPreference } from "../../hooks/useDetailPreference";
import styles from "./MonEquipePage.module.scss";

export default function MonEquipePage() {
  const [loading, setLoading] = useState(true);
  const [subordonnes, setSubordonnes] = useState([]);
  const [selected, setSelected] = useState(null);
  const { mode } = useDetailPreference();

  const load = () => {
    setLoading(true);
    PersonnelService.getInstance()
      .mesSubordonnes()
      .then(r => setSubordonnes(r.data))
      .catch(catchError)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <Main>
      <div className={styles.page}>
        <div className={styles.hero}>
          <div>
            <div className={styles.kicker}>Management</div>
            <PageTitle
              title="Mon équipe"
              subTitle="Les collaborateurs dont vous êtes le supérieur hiérarchique direct"
            />
          </div>
          <div className={styles.heroActions}>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={load} className={styles.refreshBtn}>
              Rafraîchir
            </Button>
          </div>
        </div>

        <div className={styles.statsGrid}>
          <Card className={styles.statCard} bordered={false} styles={{ body: { padding: 14 } }}>
            <div className={styles.statLabel}>Collaborateurs</div>
            <div className={styles.statValue}>{subordonnes.length}</div>
            <div className={styles.statHint}>sous votre responsabilité</div>
          </Card>
          <Card className={styles.statCard} bordered={false} styles={{ body: { padding: 14 } }}>
            <div className={styles.statLabel}>Actifs</div>
            <div className={styles.statValue}>{subordonnes.filter(p => p.enabled).length}</div>
            <div className={styles.statHint}>comptes applicatifs actifs</div>
          </Card>
          <Card className={styles.statCard} bordered={false} styles={{ body: { padding: 14 } }}>
            <div className={styles.statLabel}>Alertes</div>
            <div className={styles.statValue}>{subordonnes.reduce((acc, p) => acc + (p.taches_en_retard_count ?? 0), 0)}</div>
            <div className={styles.statHint}>tâches en retard cumulées</div>
          </Card>
        </div>
      </div>

      <div className={styles.listSection}>
        <Spin spinning={loading}>
          {subordonnes.length === 0 && !loading ? (
            <div className={styles.emptyWrap}>
              <Empty
                description="Vous n'avez aucun collaborateur sous votre responsabilité directe"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            <Row gutter={[18, 18]} className={styles.grid}>
              {subordonnes.map(personnel => (
                <Col xs={24} sm={12} lg={8} key={personnel.uid}>
                  <Card
                    hoverable
                    onClick={() => setSelected(personnel)}
                    className={styles.personnelCard}
                    styles={{ body: { display: 'flex', flexDirection: 'column', gap: 8, padding: 12 } }}
                  >
                    <div className={styles.personnelHeader}>
                      <Avatar size={42} src={personnel.avatar_link} icon={<TeamOutlined />}>
                        {personnel.prenoms?.[0]}
                      </Avatar>
                      <div className={styles.personnelIdentity}>
                        <div className={styles.personnelName}>{personnel.name}</div>
                        <div className={styles.personnelDept}>
                          {personnel.departement?.nom ?? 'Sans département'}
                        </div>
                      </div>
                      <Tag color={personnel.enabled ? 'green' : 'default'} className={styles.statusTag}>
                        {personnel.enabled ? 'Actif' : 'Inactif'}
                      </Tag>
                    </div>

                    <div className={styles.miniStats}>
                      <Tooltip title="Tâches en cours">
                        <Tag icon={<CheckSquareOutlined />} className={styles.metricTag}>
                          {personnel.taches_en_cours_count}
                        </Tag>
                      </Tooltip>
                      {personnel.taches_en_retard_count > 0 && (
                        <Tooltip title="Tâches en retard">
                          <Tag icon={<ClockCircleOutlined />} color="red" className={styles.metricTag}>
                            {personnel.taches_en_retard_count} en retard
                          </Tag>
                        </Tooltip>
                      )}
                      <Tooltip title={personnel.dossiers_responsable_count === null
                        ? "Pas de compte applicatif"
                        : "Dossiers dont il est responsable"}>
                        <Tag icon={<FolderOpenOutlined />} className={styles.metricTag}>
                          {personnel.dossiers_responsable_count ?? 'N/A'}
                        </Tag>
                      </Tooltip>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Spin>
      </div>

      <PersonnelQuickView
        personnel={selected}
        mode={mode}
        onClose={() => setSelected(null)}
      />
    </Main>
  );
}
